import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { onAuthStateChanged, type User } from 'firebase/auth'
import { Link, Navigate, Route, Routes } from 'react-router-dom'
import {
  ArrowRight,
  Check,
  Cloud,
  Download,
  ExternalLink,
  ListChecks,
  Share2,
  ShieldCheck,
  ShoppingBasket,
  Smartphone,
  Store,
  Trash2,
  Users,
  WifiOff,
} from 'lucide-react'
import { firebaseConfigured, requireAuth } from './firebase'
import { accountSignOut, deleteCurrentAccount, emailSignIn, googleSignIn } from './data'
import ParityPwa from './pwa/ParityPwa'
import './marketing.css'

const publicOrigin = import.meta.env.VITE_PUBLIC_ORIGIN || 'https://shelfway.nmless.xyz'
const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL || ''
const googlePlayUrl = 'https://play.google.com/store/apps/details?id=se.hampusandersson.aisle'
const brandLogo = '/brand/shelfway-logo.png'

function setMeta(selector: string, attribute: string, value: string) {
  const element = document.head.querySelector(selector)
  if (element) element.setAttribute(attribute, value)
}

function usePageMeta(title: string, description: string, path: string) {
  useEffect(() => {
    document.title = title
    setMeta('meta[name="description"]', 'content', description)
    setMeta('meta[property="og:title"]', 'content', title)
    setMeta('meta[property="og:description"]', 'content', description)
    setMeta('meta[property="og:url"]', 'content', `${publicOrigin}${path}`)
    setMeta('meta[name="twitter:title"]', 'content', title)
    setMeta('meta[name="twitter:description"]', 'content', description)
    setMeta('link[rel="canonical"]', 'href', `${publicOrigin}${path}`)
  }, [description, path, title])
}

function Brand({ compact = false }: { compact?: boolean }) {
  return <Link className={`brand ${compact ? 'brand-compact' : ''}`} to="/" aria-label="Shelfway home">
    <img src={brandLogo} alt="" />
    <span><strong>SHELFWAY</strong>{!compact && <small>Shop the smart way.</small>}</span>
  </Link>
}

function PublicHeader() {
  return <header className="marketing-header">
    <div className="marketing-header-inner">
      <Brand />
      <nav aria-label="Main navigation">
        <a href="/#features">Features</a>
        <a href="/#platforms">Platforms</a>
        <Link to="/privacy">Privacy</Link>
        <Link to="/support">Support</Link>
      </nav>
      <div className="marketing-header-actions">
        <Link className="button button-secondary button-small" to="/pwa/">Web app</Link>
        <a className="button button-small" href={googlePlayUrl} target="_blank" rel="noreferrer">Google Play</a>
      </div>
    </div>
  </header>
}

function PublicFooter() {
  return <footer className="marketing-footer">
    <Brand compact />
    <div className="marketing-footer-links">
      <Link to="/privacy">Privacy policy</Link>
      <Link to="/delete-account">Delete account</Link>
      <Link to="/support">Support</Link>
      <Link to="/pwa/">Web app</Link>
    </div>
    <small>© {new Date().getFullYear()} Shelfway</small>
  </footer>
}

function StoreCta() {
  return <a className="store-cta" href={googlePlayUrl} target="_blank" rel="noreferrer" aria-label="Get Shelfway on Google Play">
    <Download aria-hidden="true" />
    <span className="store-cta-copy"><small>Get Shelfway on</small><strong>Google Play</strong></span>
  </a>
}

function Landing() {
  usePageMeta(
    'Shelfway – Smart shopping lists for Android, iPhone & web',
    'Shelfway puts your shopping list in store order and keeps shared lists synchronized in realtime across Android, iPhone, iPad and the web.',
    '/',
  )

  const features = [
    { icon: <ListChecks/>, title: 'Shop in store order', text: 'Use a store shopping order to group items in the sequence you actually meet them while walking through the store.' },
    { icon: <Share2/>, title: 'Realtime shared lists', text: 'Invite another Shelfway account and shop from the same live list. Checks, edits and new items stay synchronized.' },
    { icon: <Store/>, title: 'Reusable store layouts', text: 'Save stores and shopping orders once, then reuse them whenever you create a new shopping list.' },
    { icon: <Cloud/>, title: 'Your lists on every device', text: 'Sign in with the same Shelfway account to keep your shopping data available across supported devices.' },
    { icon: <WifiOff/>, title: 'Designed for unreliable signal', text: 'Previously loaded data remains useful when reception drops, with supported changes synchronizing again when connectivity returns.' },
    { icon: <Users/>, title: 'Controlled collaboration', text: 'List owners manage invitations and access, while accepted collaborators work from the same canonical shared list.' },
  ]

  return <div className="public-page marketing-page">
    <PublicHeader />
    <main className="marketing-main">
      <section className="marketing-hero">
        <div className="marketing-hero-copy">
          <div className="hero-product-lockup">
            <img src={brandLogo} alt="Shelfway app icon" />
            <div><strong>SHELFWAY</strong><span>Shop the smart way.</span></div>
          </div>
          <div className="marketing-eyebrow"><span className="marketing-eyebrow-dot"/>Smart shopping lists for Android, iPhone & web</div>
          <h1>Your shopping list, in the order you actually shop.</h1>
          <p>Shelfway reduces backtracking by arranging items around the store route you choose. Share a list with someone else and both accounts see the same changes as you shop.</p>
          <div className="marketing-hero-actions">
            <StoreCta />
            <Link className="web-cta" to="/pwa/"><Smartphone size={22}/> Use on iPhone, iPad or web <ArrowRight size={18}/></Link>
          </div>
          <div className="marketing-platform-note"><Check/> Native on Android. Installable from Safari on iPhone and iPad.</div>
          <div className="marketing-trust">
            <span><Share2/> Realtime collaboration</span>
            <span><ShieldCheck/> Account-based access</span>
            <span><ListChecks/> Store-order shopping</span>
          </div>
        </div>
        <div className="marketing-device" aria-hidden="true">
          <div className="device-chip device-chip-top"><Share2/> Live shared list</div>
          <div className="marketing-phone">
            <div className="marketing-phone-bar"><span>09:41</span><div className="marketing-phone-brand"><img src={brandLogo} alt=""/>SHELFWAY</div></div>
            <div className="marketing-preview">
              <h3>Weekly groceries</h3>
              <div className="marketing-preview-section">Produce</div>
              <div className="marketing-preview-row checked"><span className="marketing-preview-check"/>Bananas</div>
              <div className="marketing-preview-row"><span className="marketing-preview-check"/>Tomatoes</div>
              <div className="marketing-preview-row"><span className="marketing-preview-check"/>Avocado</div>
              <div className="marketing-preview-section">Dairy & eggs</div>
              <div className="marketing-preview-row"><span className="marketing-preview-check"/>Milk · 2 packs</div>
              <div className="marketing-preview-row"><span className="marketing-preview-check"/>Eggs</div>
              <div className="marketing-preview-row"><span className="marketing-preview-check"/>Greek yoghurt</div>
            </div>
          </div>
          <div className="device-chip device-chip-bottom"><ListChecks/> Sorted by store order</div>
        </div>
      </section>

      <section id="features" className="marketing-section">
        <div className="marketing-section-heading">
          <div className="marketing-eyebrow">Built for the shop itself</div>
          <h2>A shopping list that understands where things are.</h2>
          <p>Shelfway combines ordinary lists with a reusable store order, so the list becomes a route instead of a pile of items.</p>
        </div>
        <div className="marketing-feature-grid">
          {features.map(feature => <article className="marketing-feature" key={feature.title}>
            <div className="marketing-feature-icon">{feature.icon}</div>
            <h3>{feature.title}</h3><p>{feature.text}</p>
          </article>)}
        </div>
      </section>

      <section className="workflow-band">
        <div className="marketing-section workflow-section">
          <div className="marketing-section-heading">
            <div className="marketing-eyebrow">From list to checkout</div>
            <h2>Set the route once. Shop it every week.</h2>
          </div>
          <div className="workflow-grid">
            <article><span>01</span><h3>Create your store order</h3><p>Arrange categories in the same sequence you normally meet them in the store.</p></article>
            <article><span>02</span><h3>Add what you need</h3><p>Shelfway groups the list around that route instead of leaving everything in entry order.</p></article>
            <article><span>03</span><h3>Shop together</h3><p>Invite another account and both of you work from the same realtime list while shopping.</p></article>
          </div>
        </div>
      </section>

      <section id="platforms" className="platform-band">
        <div className="marketing-section">
          <div className="marketing-section-heading">
            <div className="marketing-eyebrow">Use Shelfway your way</div>
            <h2>Android, iPhone, iPad and desktop.</h2>
            <p>The same Shelfway account can be used across the native Android app and the installable web experience.</p>
          </div>
          <div className="platform-grid">
            <article className="platform-card platform-card-primary">
              <div className="platform-card-icon"><Smartphone/></div><div className="platform-status">Android · available now</div><h3>Native Android app</h3>
              <p>Install Shelfway from Google Play for the native Android experience and automatic Play Store updates.</p>
              <a href={googlePlayUrl} target="_blank" rel="noreferrer">Open Google Play <ExternalLink/></a>
            </article>
            <article className="platform-card">
              <div className="platform-card-icon"><Smartphone/></div><div className="platform-status">iPhone & iPad</div><h3>Install from Safari</h3>
              <p>Open Shelfway in Safari, choose Share, then Add to Home Screen for an app-like icon and standalone experience.</p>
              <Link to="/pwa/">Open Shelfway for iPhone <ArrowRight/></Link>
            </article>
            <article className="platform-card">
              <div className="platform-card-icon"><Cloud/></div><div className="platform-status">Desktop & web</div><h3>Use it in your browser</h3>
              <p>Sign in from a modern browser when you want your Shelfway lists on a laptop, Chromebook or another device.</p>
              <Link to="/pwa/">Open the web app <ArrowRight/></Link>
            </article>
          </div>
        </div>
      </section>

      <section className="marketing-section security-section-wrap">
        <div className="marketing-security">
          <div>
            <div className="marketing-security-icon"><ShieldCheck/></div>
            <h2>Clear privacy and account controls.</h2>
            <p>Shelfway publishes its privacy information and provides a web account-deletion route so you can manage your account even if the app is no longer installed.</p>
          </div>
          <div className="marketing-security-links">
            <Link to="/privacy"><span>Read the privacy policy</span><ArrowRight/></Link>
            <Link to="/delete-account"><span>Delete a Shelfway account</span><ArrowRight/></Link>
            <Link to="/support"><span>Get support</span><ArrowRight/></Link>
          </div>
        </div>
      </section>

      <section className="marketing-final-cta">
        <div className="marketing-final-logo"><img src={brandLogo} alt="Shelfway app icon"/></div>
        <div className="marketing-final-copy"><h2>Shop the smart way.</h2><p>Start on Android, iPhone, iPad or the web with one Shelfway account.</p></div>
        <div className="marketing-final-actions"><StoreCta/><Link className="button button-secondary" to="/pwa/">Open web app</Link></div>
      </section>
    </main>
    <PublicFooter />
  </div>
}

function Privacy() {
  usePageMeta('Shelfway Privacy Policy', 'How Shelfway collects, uses, shares, protects, retains and deletes account and shopping data.', '/privacy')
  return <div className="public-page marketing-page"><PublicHeader/><main className="legal marketing-legal">
    <span className="legal-kicker">Privacy policy</span>
    <h1>Shelfway Privacy Policy</h1>
    <p className="lead">This policy explains how Shelfway handles information in the Android app and Shelfway web experience.</p>
    <p><strong>Last updated:</strong> 20 August 2026</p>
    <p className="policy-note">Shelfway uses account data to provide synchronized shopping lists, store layouts and collaboration. A list is not shared with another Shelfway account unless access is granted through the collaboration/invitation system.</p>

    <h2>Information Shelfway processes</h2>
    <div className="policy-table">
      <div className="policy-row"><strong>Account information</strong><span>Email address, Firebase user ID, authentication provider and account settings needed to sign in and identify your Shelfway account.</span></div>
      <div className="policy-row"><strong>Shopping content</strong><span>Shopping lists, list items, checked state, notes, quantities, categories, stores, store shopping orders/layouts and synchronization timestamps.</span></div>
      <div className="policy-row"><strong>Collaboration data</strong><span>List ownership, member IDs, invitation status and email information required to invite the intended Shelfway account and manage list access.</span></div>
      <div className="policy-row"><strong>Device and technical data</strong><span>Installation/device identifiers used for app functionality such as invitation notifications, plus technical synchronization information.</span></div>
      <div className="policy-row"><strong>Feedback</strong><span>Category, title, message and diagnostic context such as app version, platform, locale and screen when you choose to submit feedback.</span></div>
      <div className="policy-row"><strong>Local data</strong><span>The Android app stores data locally for offline use. The web app may use browser storage and Firestore's persistent cache for offline-capable behavior and local preferences.</span></div>
    </div>

    <h2>Why Shelfway uses this information</h2>
    <ul>
      <li>Authenticate your account and maintain account settings.</li>
      <li>Synchronize your shopping data between supported devices.</li>
      <li>Create, organize, categorize and display shopping lists in store order.</li>
      <li>Enable invitations, shared lists and realtime collaboration.</li>
      <li>Provide offline-capable behavior and resolve synchronization changes.</li>
      <li>Deliver requested app functionality such as collaboration notifications.</li>
      <li>Respond to feedback, troubleshoot defects and maintain service security.</li>
    </ul>

    <h2>Services and parties that may receive data</h2>
    <p>Shelfway uses Google Firebase services, including Firebase Authentication and Cloud Firestore, to provide authentication, synchronized storage and related app infrastructure. Google processes data on Shelfway's behalf as a service provider according to the applicable Google/Firebase terms and security practices.</p>
    <p>When you accept or use a shared shopping list, information in that list is available to the other Shelfway accounts that currently have access. Collaboration identity information may also be visible where needed to show who owns, invited or has access to a shared list.</p>
    <p>Shelfway does not make a private shopping list public merely because it is synchronized to the cloud.</p>

    <h2>Security</h2>
    <p>Shelfway uses authenticated Firebase access and Firestore security rules to restrict cloud data according to account ownership and list membership. Network traffic to Shelfway's hosted services uses HTTPS/TLS. Access credentials and privileged Android signing or server credentials are not embedded into the public website.</p>

    <h2>Retention and deletion</h2>
    <p>Account-associated cloud data is retained while it is needed to provide your Shelfway account and its features. You can initiate deletion inside Shelfway or through the public <Link to="/delete-account">Delete account</Link> page without reinstalling the Android app.</p>
    <p>Account deletion removes the Firebase Authentication account and Shelfway account-associated cloud records handled by the deletion flow, including profile information, owned shopping-list data, invitations, collaboration membership links, user-created layout data and registered installation identifiers. Local app or browser caches can also be removed by clearing the app/site data or uninstalling the app.</p>
    <p>If information must be retained for a legitimate legal, security or fraud-prevention reason, it will be retained only for the purpose and period reasonably required.</p>

    <h2>Your choices</h2>
    <ul>
      <li>Choose whether to create and share lists.</li>
      <li>Owners can manage collaborator access to their lists.</li>
      <li>Delete your Shelfway account and associated account data.</li>
      <li>Clear local app/browser storage from your device.</li>
    </ul>

    <h2>Contact</h2>
    <p>For privacy questions or requests, use the <Link to="/support">Shelfway support page</Link>{supportEmail ? <> or email <a href={`mailto:${supportEmail}`}>{supportEmail}</a></> : <>. Shelfway's public support email is shown here when the production support address is configured.</>}.</p>
    <p>This privacy policy applies to <strong>Shelfway</strong> and is published at a publicly accessible website intended for the app's users and Google Play listing.</p>
  </main><PublicFooter/></div>
}

function Support() {
  usePageMeta('Shelfway Support', 'Help with Shelfway on Android, iPhone, iPad and web, including accounts, synchronization and shared shopping lists.', '/support')
  return <div className="public-page marketing-page"><PublicHeader/><main className="legal marketing-legal">
    <span className="legal-kicker">Support</span><h1>Shelfway Support</h1>
    <p className="lead">Help with installing Shelfway, your account, synchronized lists and collaboration.</p>
    <div className="support-grid">
      <div className="support-card"><h2>Android</h2><p>Install or update the native Shelfway app from Google Play.</p></div>
      <div className="support-card"><h2>iPhone & iPad</h2><p>Open Shelfway in Safari, tap Share, then choose Add to Home Screen.</p></div>
      <div className="support-card"><h2>Shared lists</h2><p>Both accounts must be signed in. Accept a pending invitation before the shared list appears.</p></div>
      <div className="support-card"><h2>Realtime sync</h2><p>Signed-in devices use the same cloud list. Offline changes synchronize when connectivity is available.</p></div>
    </div>
    <h2>Install Shelfway</h2>
    <p><a href={googlePlayUrl} target="_blank" rel="noreferrer">Android: open Shelfway on Google Play</a>.</p>
    <p>iPhone/iPad: open <strong>{publicOrigin}/pwa/</strong> in Safari → Share → <strong>Add to Home Screen</strong>.</p>
    <h2>Account deletion</h2>
    <p>If you no longer have the app installed, you can still delete your account using the public <Link to="/delete-account">Shelfway account-deletion page</Link>.</p>
    <h2>Contact and feedback</h2>
    {supportEmail
      ? <p>Email <a href={`mailto:${supportEmail}`}>{supportEmail}</a>. This should be the same actively monitored support address used in the Google Play listing.</p>
      : <p>For product feedback while signed in, open Shelfway → Settings → Feedback. The production website can also expose the public Play support address here by configuring <code>VITE_SUPPORT_EMAIL</code>.</p>}
    <p><Link className="button" to="/pwa/settings">Open Shelfway settings</Link></p>
  </main><PublicFooter/></div>
}

function Field({ label, children }: { label: string; children: ReactNode }) { return <label className="field"><span>{label}</span>{children}</label> }
function Notice({ children }: { children: ReactNode }) { return <div className="notice">{children}</div> }

function DeleteAccountPublic() {
  usePageMeta('Delete your Shelfway account', 'Delete a Shelfway account and its associated account data from the web without reinstalling the app.', '/delete-account')
  const [user, setUser] = useState<User | null>(null)
  const [authLoaded, setAuthLoaded] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  useEffect(() => {
    if (!firebaseConfigured) { setAuthLoaded(true); return }
    return onAuthStateChanged(requireAuth(), next => { setUser(next); setAuthLoaded(true) })
  }, [])
  const run = async (operation: () => Promise<unknown>) => { setBusy(true); setMessage(''); try { await operation() } catch (error) { setMessage(error instanceof Error ? error.message : String(error)) } finally { setBusy(false) } }
  const login = (event: FormEvent) => { event.preventDefault(); void run(() => emailSignIn(email, password)) }
  const remove = async () => {
    if (!confirm('Permanently delete your Shelfway account and account-associated data? This cannot be undone.')) return
    await run(async () => { await deleteCurrentAccount(); setMessage('Your Shelfway account and associated account data have been deleted.') })
  }
  return <div className="public-page marketing-page"><PublicHeader/><main className="legal marketing-legal narrow">
    <span className="legal-kicker">Account deletion</span><h1>Delete your Shelfway account</h1>
    <p className="lead">This is Shelfway's external account-deletion route. You do not need to reinstall the Android app to use it.</p>
    <div className="delete-summary"><h2>What deletion does</h2><ul><li>Deletes your Shelfway authentication account.</li><li>Deletes account-associated Shelfway cloud data handled by the deletion flow.</li><li>Removes your collaboration membership references and invitations.</li><li>Deletes owned list data and registered installation identifiers handled by the deletion flow.</li></ul></div>
    <p className="delete-warning">Deletion is permanent. Content owned by another user is controlled by that list owner; your account's access/membership is removed as part of deletion.</p>
    {!firebaseConfigured ? <Notice>This deployment is missing its Firebase web configuration, so account deletion cannot be started here until deployment configuration is restored.</Notice> : !authLoaded ? <p>Loading…</p> : !user ? <form className="panel form-stack" onSubmit={login}>
      <div className="auth-icon"><ShoppingBasket/></div><h2>Sign in to verify the account</h2>
      <Field label="Email"><input type="email" autoComplete="email" value={email} onChange={event=>setEmail(event.target.value)} required/></Field>
      <Field label="Password"><input type="password" autoComplete="current-password" value={password} onChange={event=>setPassword(event.target.value)} required/></Field>
      <button className="button" disabled={busy}>Sign in</button>
      <button type="button" className="button button-secondary" disabled={busy} onClick={()=>void run(()=>googleSignIn())}>Continue with Google</button>
    </form> : <div className="danger-zone"><h2>Signed in as {user.email}</h2><p>Deleting this account permanently removes the Shelfway account and account-associated cloud data processed by this deletion flow.</p><button className="button button-danger" disabled={busy} onClick={()=>void remove()}><Trash2 size={18}/> Delete account permanently</button><button className="text-button" onClick={()=>void accountSignOut()}>Sign out</button></div>}
    {message&&<Notice>{message}</Notice>}
    <h2>Need help first?</h2><p>See <Link to="/support">Shelfway Support</Link>{supportEmail ? <> or contact <a href={`mailto:${supportEmail}`}>{supportEmail}</a></> : null}.</p>
  </main><PublicFooter/></div>
}

export default function App() {
  return <Routes>
    <Route path="/pwa/*" element={<ParityPwa/>}/>
    <Route path="/" element={<Landing/>}/>
    <Route path="/privacy" element={<Privacy/>}/>
    <Route path="/support" element={<Support/>}/>
    <Route path="/delete-account" element={<DeleteAccountPublic/>}/>
    <Route path="*" element={<Navigate to="/" replace/>}/>
  </Routes>
}